import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import UserAvatar from "@/components/UserAvatar";
import { toast } from "sonner";
import {
  BookOpenText, Plus, Trash, PencilSimple, UploadSimple, Camera, X, Heart, ChatCircle,
  CaretLeft, CaretRight, WhatsappLogo, PaperPlaneRight, ShieldStar, MagnifyingGlass,
  UserPlus, PlayCircle, ImageSquare, Play, ArrowsClockwise,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 10;
const MAX_VIDEOS = 3;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "ahora";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString("es-MX");
}

function roleColor(r) {
  if (r === "admin") return "text-[#CCFF00]";
  if (r === "directivo") return "text-blue-400";
  if (r === "profesor") return "text-yellow-400";
  return "text-gray-400";
}

export default function Comics() {
  const { user } = useAuth();
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [canPublish, setCanPublish] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [viewerId, setViewerId] = useState(null);
  const [publishersOpen, setPublishersOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comics?limit=60");
      setComics(data || []);
    } catch { toast.error("Error al cargar cómics"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!user) return;
    loadList();
    api.get("/comics/permission/me").then(({ data }) => setCanPublish(!!data?.can_publish)).catch(() => {});
  }, [user, loadList]);

  return (
    <div className="max-w-5xl mx-auto space-y-4 pb-8" data-testid="comics-page">
      <header className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#CCFF00] font-bold flex items-center gap-1.5">
            <BookOpenText size={12} weight="fill" /> Comunidad · Cómic
          </p>
          <h1 className="font-['Barlow_Condensed'] text-3xl sm:text-4xl font-black uppercase tracking-tighter leading-none mt-1">
            Historias en viñetas
          </h1>
          <p className="text-xs text-gray-400 mt-1">Cómics multimedia publicados por el administrador.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setPublishersOpen(true)}
              className="border-white/15 text-gray-300 hover:text-[#CCFF00] text-[10px] uppercase h-9"
              data-testid="comics-publishers-btn"
            >
              <ShieldStar size={14} className="mr-1" /> Autores
            </Button>
          )}
          {canPublish && (
            <Button
              onClick={() => setUploadOpen(true)}
              className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide"
              data-testid="comics-new-btn"
            >
              <Plus size={14} className="mr-1" /> Nuevo cómic
            </Button>
          )}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
        </div>
      ) : comics.length === 0 ? (
        <div className="card-tactical p-10 text-center" data-testid="comics-empty">
          <BookOpenText size={44} className="mx-auto text-gray-600 mb-2" />
          <p className="text-sm text-gray-400">Todavía no hay cómics publicados.</p>
          {canPublish && (
            <Button onClick={() => setUploadOpen(true)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase mt-3">
              <Plus size={14} className="mr-1" /> Publicar el primero
            </Button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="comics-grid">
          {comics.map((c) => (
            <ComicCard key={c.id} comic={c} onOpen={() => setViewerId(c.id)} />
          ))}
        </div>
      )}

      <UploadComicDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onCreated={() => { setUploadOpen(false); loadList(); }}
      />

      <ComicViewer
        comicId={viewerId}
        onClose={() => setViewerId(null)}
        onDeleted={() => { setViewerId(null); loadList(); }}
        onEdited={loadList}
        currentUser={user}
      />

      {isAdmin && (
        <PublishersDialog
          open={publishersOpen}
          onClose={() => setPublishersOpen(false)}
        />
      )}
    </div>
  );
}

// ============================================================
// COMIC CARD
// ============================================================
function ComicCard({ comic, onOpen }) {
  const cover = (comic.panels && comic.panels[0]) || null;
  const isVideoCover = cover && cover.media_type === "video";
  const coverUrl = cover ? `${BACKEND_URL}/api/comics/${comic.id}/panels/${cover.id}/media` : "";
  return (
    <article
      className="card-tactical overflow-hidden cursor-pointer group hover:border-[#CCFF00]/40 transition-colors"
      onClick={onOpen}
      data-testid={`comic-card-${comic.id}`}
    >
      <div className="relative aspect-[3/4] bg-black overflow-hidden">
        {cover ? (
          isVideoCover ? (
            <video src={coverUrl} muted className="w-full h-full object-cover" playsInline preload="metadata">
              <track kind="captions" />
            </video>
          ) : (
            <img src={coverUrl} alt={comic.title || "cómic"} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          )
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600">
            <BookOpenText size={40} />
          </div>
        )}
        {isVideoCover && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <PlayCircle size={40} weight="fill" className="text-white/90 drop-shadow-lg" />
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 flex gap-1">
          <Badge className="bg-black/70 text-[9px] uppercase border-white/10 tracking-widest">
            <ImageSquare size={9} className="mr-0.5" /> {comic.panels_count || (comic.panels || []).length}
          </Badge>
        </div>
      </div>
      <div className="p-2.5 space-y-1">
        <h3 className="text-sm font-bold text-white leading-tight line-clamp-2 group-hover:text-[#CCFF00] transition-colors" data-testid={`comic-title-${comic.id}`}>
          {comic.title || "Sin título"}
        </h3>
        <div className="flex items-center gap-1.5 text-[10px] text-gray-500">
          <span className={`font-semibold ${roleColor(comic.author_role)}`}>{comic.author_name || "Admin"}</span>
          <span>·</span>
          <span>{timeAgo(comic.created_at)}</span>
        </div>
        <div className="flex items-center gap-3 pt-0.5 text-[10px] text-gray-400">
          <span className="flex items-center gap-0.5"><Heart size={10} /> {(comic.likes || []).length}</span>
          <span className="flex items-center gap-0.5"><ChatCircle size={10} /> {comic.comment_count || 0}</span>
        </div>
      </div>
    </article>
  );
}

// ============================================================
// UPLOAD DIALOG
// ============================================================
function UploadComicDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [items, setItems] = useState([]); // {file, url, caption, kind: 'image'|'video'}
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);
  const camRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setTitle(""); setCaption(""); setItems([]); setSubmitting(false);
    }
  }, [open]);

  const currentImages = items.filter((i) => i.kind === "image").length;
  const currentVideos = items.filter((i) => i.kind === "video").length;

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const next = [...items];
    let addedI = currentImages;
    let addedV = currentVideos;
    let rejected = 0;
    for (const f of arr) {
      const kind = (f.type || "").startsWith("video/") ? "video" : "image";
      if (kind === "image" && addedI >= MAX_IMAGES) { rejected += 1; continue; }
      if (kind === "video" && addedV >= MAX_VIDEOS) { rejected += 1; continue; }
      next.push({ file: f, url: URL.createObjectURL(f), caption: "", kind });
      if (kind === "image") addedI += 1; else addedV += 1;
    }
    if (rejected > 0) toast.warning(`${rejected} archivo(s) descartados por exceder límite (10 fotos / 3 videos)`);
    setItems(next);
  };

  const removeItem = (idx) => {
    setItems((prev) => {
      const next = [...prev];
      try { URL.revokeObjectURL(next[idx]?.url); } catch { /* noop */ }
      next.splice(idx, 1);
      return next;
    });
  };

  const move = (idx, dir) => {
    setItems((prev) => {
      const to = idx + dir;
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(idx, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const submit = async () => {
    if (items.length === 0) { toast.error("Agrega al menos un panel"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("caption", caption);
      fd.append("panel_captions", JSON.stringify(items.map((i) => i.caption || "")));
      items.forEach((i) => fd.append("files", i.file, i.file.name));
      await api.post("/comics", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success("Cómic publicado");
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al publicar");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="comics-upload-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Nuevo cómic</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título del cómic (opcional)"
            className="bg-black/60 border-white/15 text-sm"
            maxLength={200}
            data-testid="comic-title-input"
          />
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Descripción o sinopsis (opcional)…"
            rows={2}
            className="bg-black/60 border-white/15 text-sm"
            data-testid="comic-caption-input"
          />

          <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-400">
            <span>Paneles</span>
            <span>
              <span className={currentImages >= MAX_IMAGES ? "text-red-400" : "text-[#CCFF00]"}>{currentImages}</span>/{MAX_IMAGES} fotos ·{" "}
              <span className={currentVideos >= MAX_VIDEOS ? "text-red-400" : "text-[#CCFF00]"}>{currentVideos}</span>/{MAX_VIDEOS} videos
            </span>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => fileRef.current?.click()}
              className="flex-1 border-[#CCFF00]/40 text-[#CCFF00] h-10 text-xs uppercase"
              data-testid="comic-upload-files"
            >
              <UploadSimple size={16} className="mr-1" /> Elegir archivos
            </Button>
            <Button
              variant="outline"
              onClick={() => camRef.current?.click()}
              className="flex-1 border-[#FFD24A]/40 text-[#FFD24A] h-10 text-xs uppercase"
              data-testid="comic-upload-camera"
            >
              <Camera size={16} className="mr-1" /> Cámara
            </Button>
            <input ref={fileRef} type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="comic-file-input" />
            <input ref={camRef} type="file" accept="image/*,video/*" capture="environment" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="comic-camera-input" />
          </div>

          {items.length > 0 && (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="comic-panels-list">
              {items.map((it, idx) => (
                <div key={idx} className="flex gap-2 p-2 bg-black/40 border border-white/10 rounded" data-testid={`comic-panel-${idx}`}>
                  <div className="w-16 h-16 bg-black relative shrink-0 overflow-hidden rounded border border-white/10">
                    {it.kind === "video" ? (
                      <>
                        <video src={it.url} muted className="w-full h-full object-cover">
                          <track kind="captions" />
                        </video>
                        <div className="absolute inset-0 flex items-center justify-center bg-black/40"><Play size={16} weight="fill" className="text-white" /></div>
                      </>
                    ) : (
                      <img src={it.url} alt={`p${idx}`} className="w-full h-full object-cover" />
                    )}
                    <Badge className="absolute top-0.5 left-0.5 bg-black/70 text-[8px] px-1 py-0 h-4 border-white/10">#{idx + 1}</Badge>
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <p className="text-[10px] text-gray-400 truncate">{it.file.name} · {(it.file.size / 1024 / 1024).toFixed(1)}MB</p>
                    <Input
                      value={it.caption}
                      onChange={(e) => setItems((prev) => prev.map((x, i) => i === idx ? { ...x, caption: e.target.value } : x))}
                      placeholder="Diálogo o texto del panel…"
                      className="bg-black/60 border-white/10 h-7 text-[11px]"
                      maxLength={500}
                      data-testid={`comic-panel-caption-${idx}`}
                    />
                  </div>
                  <div className="flex flex-col gap-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400" onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`comic-panel-up-${idx}`}><CaretLeft size={12} className="rotate-90" /></Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-gray-400" onClick={() => move(idx, +1)} disabled={idx === items.length - 1} data-testid={`comic-panel-down-${idx}`}><CaretLeft size={12} className="-rotate-90" /></Button>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeItem(idx)} data-testid={`comic-panel-remove-${idx}`}><X size={12} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-xs uppercase" data-testid="comic-upload-cancel">Cancelar</Button>
          <Button
            onClick={submit}
            disabled={submitting || items.length === 0}
            className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase"
            data-testid="comic-upload-submit"
          >
            {submitting ? "Publicando…" : `Publicar cómic (${items.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// COMIC VIEWER (Carousel of panels)
// ============================================================
function ComicViewer({ comicId, onClose, onDeleted, onEdited, currentUser }) {
  const [comic, setComic] = useState(null);
  const [idx, setIdx] = useState(0);
  const [liking, setLiking] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ title: "", caption: "" });
  const touchStartX = useRef(null);

  useEffect(() => {
    if (!comicId) { setComic(null); setIdx(0); setComments([]); setShowComments(false); return; }
    api.get(`/comics/${comicId}`).then(({ data }) => { setComic(data); setIdx(0); }).catch(() => { toast.error("No se pudo abrir el cómic"); onClose?.(); });
  }, [comicId, onClose]);

  useEffect(() => {
    const onKey = (e) => {
      if (!comic) return;
      if (e.key === "ArrowLeft") setIdx((i) => Math.max(0, i - 1));
      else if (e.key === "ArrowRight") setIdx((i) => Math.min((comic.panels?.length || 1) - 1, i + 1));
      else if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [comic, onClose]);

  const canEdit = comic && currentUser && (comic.author_id === currentUser.id || currentUser.role === "admin");
  const iLike = comic && (comic.likes || []).includes(currentUser?.id);

  const panels = comic?.panels || [];
  const current = panels[idx];
  const total = panels.length;

  const toggleLike = async () => {
    if (!comic || liking) return;
    setLiking(true);
    try {
      const { data } = await api.post(`/comics/${comic.id}/like`);
      setComic((c) => c ? {
        ...c,
        likes: data.liked ? [...(c.likes || []), currentUser.id] : (c.likes || []).filter((u) => u !== currentUser.id)
      } : c);
    } catch { /* noop */ } finally { setLiking(false); }
  };

  const loadComments = async () => {
    if (!comic) return;
    try { const { data } = await api.get(`/comics/${comic.id}/comments`); setComments(data || []); }
    catch { /* noop */ }
  };

  const sendComment = async () => {
    const text = newComment.trim();
    if (!text || !comic) return;
    try {
      await api.post(`/comics/${comic.id}/comments`, { text });
      setNewComment("");
      setComic((c) => c ? { ...c, comment_count: (c.comment_count || 0) + 1 } : c);
      loadComments();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al comentar"); }
  };

  const share = () => {
    if (!comic) return;
    const url = `${window.location.origin}/dashboard/comic?open=${comic.id}`;
    const text = `📖 ${comic.title || "Cómic"} — ${comic.caption || "Mira este cómic"} ${url}`;
    const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(wa, "_blank", "noopener");
  };

  const del = async () => {
    if (!comic) return;
    if (!window.confirm("¿Eliminar este cómic? Se borrarán todos sus paneles.")) return;
    try {
      await api.delete(`/comics/${comic.id}`);
      toast.success("Cómic eliminado");
      onDeleted?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const startEdit = () => {
    if (!comic) return;
    setEditData({ title: comic.title || "", caption: comic.caption || "" });
    setEditing(true);
  };

  const saveEdit = async () => {
    if (!comic) return;
    try {
      const { data } = await api.patch(`/comics/${comic.id}`, editData);
      setComic(data);
      setEditing(false);
      toast.success("Cómic actualizado");
      onEdited?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const onTouchStart = (e) => { touchStartX.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx < 0 && idx < total - 1) setIdx(idx + 1);
    else if (dx > 0 && idx > 0) setIdx(idx - 1);
  };

  return (
    <Dialog open={!!comicId} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="bg-black border-white/10 text-white max-w-3xl max-h-[95vh] overflow-hidden p-0 gap-0"
        data-testid="comic-viewer-dialog"
      >
        {!comic ? (
          <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-[#CCFF00] border-t-transparent animate-spin" /></div>
        ) : (
          <div className="flex flex-col h-full max-h-[95vh]">
            {/* Header */}
            <header className="p-3 flex items-center gap-2 border-b border-white/10 bg-[#0A0A0A]">
              <UserAvatar
                src={comic.author_photo}
                fallback={(comic.author_name || "?").slice(0, 2)}
                className="w-9 h-9 rounded-full border border-white/10"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate" data-testid="viewer-title">{comic.title || "Sin título"}</p>
                <p className="text-[10px] text-gray-500">
                  <span className={`font-semibold ${roleColor(comic.author_role)}`}>{comic.author_name}</span> · {timeAgo(comic.created_at)}
                  {comic.edited_at ? " · editado" : ""}
                </p>
              </div>
              <Badge className="bg-black/70 text-[10px] uppercase border-white/10 tracking-widest">
                Panel {idx + 1}/{total}
              </Badge>
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm" onClick={startEdit} className="text-gray-400 hover:text-[#CCFF00] h-8 w-8 p-0" data-testid="viewer-edit-btn"><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="sm" onClick={del} className="text-red-400 hover:bg-red-500/10 h-8 w-8 p-0" data-testid="viewer-delete-btn"><Trash size={14} /></Button>
                </>
              )}
              <Button variant="ghost" size="sm" onClick={onClose} className="text-gray-400 h-8 w-8 p-0" data-testid="viewer-close-btn"><X size={16} /></Button>
            </header>

            {/* Media viewport */}
            <div
              className="relative flex-1 bg-black flex items-center justify-center overflow-hidden min-h-[50vh]"
              onTouchStart={onTouchStart}
              onTouchEnd={onTouchEnd}
              data-testid="viewer-media"
            >
              {current && (
                current.media_type === "video" ? (
                  <video
                    key={current.id}
                    src={`${BACKEND_URL}/api/comics/${comic.id}/panels/${current.id}/media`}
                    controls
                    playsInline
                    autoPlay
                    className="max-w-full max-h-[70vh] object-contain"
                    data-testid={`viewer-video-${idx}`}
                  >
                    <track kind="captions" />
                  </video>
                ) : (
                  <img
                    key={current.id}
                    src={`${BACKEND_URL}/api/comics/${comic.id}/panels/${current.id}/media`}
                    alt={current.caption || `panel ${idx + 1}`}
                    className="max-w-full max-h-[70vh] object-contain"
                    data-testid={`viewer-image-${idx}`}
                  />
                )
              )}
              {/* Prev / Next */}
              {idx > 0 && (
                <button
                  onClick={() => setIdx(idx - 1)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center border border-white/10"
                  data-testid="viewer-prev"
                  aria-label="Anterior"
                >
                  <CaretLeft size={20} className="text-white" />
                </button>
              )}
              {idx < total - 1 && (
                <button
                  onClick={() => setIdx(idx + 1)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center border border-white/10"
                  data-testid="viewer-next"
                  aria-label="Siguiente"
                >
                  <CaretRight size={20} className="text-white" />
                </button>
              )}
              {current?.caption && (
                <div className="absolute left-0 right-0 bottom-0 bg-gradient-to-t from-black/95 to-transparent p-3 pt-8">
                  <p className="text-sm text-white text-center font-medium drop-shadow" data-testid={`viewer-panel-caption-${idx}`}>{current.caption}</p>
                </div>
              )}
            </div>

            {/* Dots */}
            <div className="flex items-center justify-center gap-1 py-1.5 bg-[#0A0A0A] border-t border-white/5">
              {panels.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-[#CCFF00]" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                  aria-label={`Ir al panel ${i + 1}`}
                  data-testid={`viewer-dot-${i}`}
                />
              ))}
            </div>

            {/* Footer actions */}
            <footer className="bg-[#0A0A0A] border-t border-white/10 p-3 space-y-2 max-h-[35vh] overflow-y-auto">
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleLike}
                  className={`flex items-center gap-1 text-xs uppercase tracking-widest font-bold ${iLike ? "text-red-400" : "text-gray-400 hover:text-white"}`}
                  data-testid="viewer-like-btn"
                >
                  <Heart size={16} weight={iLike ? "fill" : "regular"} /> {(comic.likes || []).length}
                </button>
                <button
                  onClick={() => { setShowComments((s) => !s); if (!showComments) loadComments(); }}
                  className="flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-gray-400 hover:text-white"
                  data-testid="viewer-comments-toggle"
                >
                  <ChatCircle size={16} /> {comic.comment_count || 0}
                </button>
                <button
                  onClick={share}
                  className="flex items-center gap-1 text-xs uppercase tracking-widest font-bold text-green-400 hover:text-green-300 ml-auto"
                  data-testid="viewer-share-btn"
                >
                  <WhatsappLogo size={16} weight="fill" /> Compartir
                </button>
              </div>
              {comic.caption && <p className="text-sm text-gray-200 whitespace-pre-wrap">{comic.caption}</p>}

              {showComments && (
                <div className="space-y-2 pt-2 border-t border-white/5" data-testid="viewer-comments-panel">
                  {comments.length === 0 ? (
                    <p className="text-[10px] text-gray-500 italic">Sé el primero en comentar.</p>
                  ) : comments.map((c) => (
                    <div key={c.id} className="flex items-start gap-2 text-xs">
                      <UserAvatar src={c.author_photo} fallback={(c.author_name || "?").slice(0, 2)} className="w-6 h-6 rounded-full shrink-0" />
                      <div className="flex-1 bg-black/40 p-2 border border-white/10 rounded">
                        <p className="text-white"><b className={roleColor(c.author_role)}>{c.author_name}</b> {c.text}</p>
                        <p className="text-[10px] text-gray-500 mt-0.5">{timeAgo(c.created_at)}</p>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-2">
                    <Input
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && sendComment()}
                      placeholder="Escribe un comentario…"
                      className="bg-black/60 border-white/15 h-9 text-xs"
                      data-testid="viewer-comment-input"
                    />
                    <Button onClick={sendComment} size="sm" className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-9 px-3" data-testid="viewer-comment-send">
                      <PaperPlaneRight size={14} />
                    </Button>
                  </div>
                </div>
              )}
            </footer>
          </div>
        )}

        {/* Edit dialog (inline) */}
        <Dialog open={editing} onOpenChange={(v) => !v && setEditing(false)}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md" data-testid="viewer-edit-dialog">
            <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase">Editar cómic</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input value={editData.title} onChange={(e) => setEditData((d) => ({ ...d, title: e.target.value }))} placeholder="Título" className="bg-black/60 border-white/15 text-sm" data-testid="edit-title" />
              <Textarea value={editData.caption} onChange={(e) => setEditData((d) => ({ ...d, caption: e.target.value }))} rows={4} placeholder="Descripción" className="bg-black/60 border-white/15 text-sm" data-testid="edit-caption" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(false)} className="text-xs uppercase">Cancelar</Button>
              <Button onClick={saveEdit} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase" data-testid="edit-save">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// PUBLISHERS (WHITELIST) DIALOG — ADMIN ONLY
// ============================================================
function PublishersDialog({ open, onClose }) {
  const [publishers, setPublishers] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/comics/publishers");
      setPublishers(data || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/comics/publishers/candidates?q=${encodeURIComponent(q)}`);
        setCandidates(data || []);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open]);

  const grant = async (userId) => {
    try {
      await api.post("/comics/publishers", { user_id: userId });
      toast.success("Permiso otorgado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const revoke = async (userId) => {
    if (!window.confirm("¿Revocar permiso a este usuario?")) return;
    try {
      await api.delete(`/comics/publishers/${userId}`);
      toast.success("Permiso revocado");
      load();
    } catch { /* noop */ }
  };

  const activeIds = new Set(publishers.map((p) => p.user_id));
  const filteredCandidates = candidates.filter((c) => !activeIds.has(c.id));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="comics-publishers-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] text-2xl font-black uppercase flex items-center gap-2">
            <ShieldStar size={20} className="text-[#CCFF00]" /> Autores de Cómic
          </DialogTitle>
          <p className="text-xs text-gray-400">El admin siempre puede publicar. Aquí puedes autorizar a otros usuarios.</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4">
          <section>
            <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1.5">Autorizados</p>
            {loading ? (
              <div className="w-5 h-5 border-2 border-[#CCFF00] border-t-transparent animate-spin" />
            ) : publishers.length === 0 ? (
              <p className="text-xs text-gray-500 italic">Nadie autorizado. Solo el admin publica.</p>
            ) : (
              <ul className="space-y-1.5" data-testid="publishers-list">
                {publishers.map((p) => (
                  <li key={p.user_id} className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded" data-testid={`publisher-${p.user_id}`}>
                    <UserAvatar src={p.user?.profile_photo_url} fallback={(p.user?.name || "?").slice(0, 2)} className="w-8 h-8 rounded-full" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{p.user?.name || "Usuario"}</p>
                      <p className="text-[10px] text-gray-500 uppercase">{p.user?.role} · Otorgado {timeAgo(p.granted_at)}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => revoke(p.user_id)} className="text-red-400 hover:bg-red-500/10 h-8 text-xs uppercase" data-testid={`publisher-revoke-${p.user_id}`}>
                      Revocar
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <p className="text-[10px] uppercase tracking-widest text-gray-400 mb-1.5">Añadir autor</p>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre o email…"
                className="pl-7 bg-black/60 border-white/15 h-9 text-xs"
                data-testid="publishers-search"
              />
            </div>
            <ul className="mt-2 space-y-1.5" data-testid="publishers-candidates">
              {filteredCandidates.length === 0 ? (
                <li className="text-xs text-gray-500 italic px-1">Sin resultados</li>
              ) : filteredCandidates.map((c) => (
                <li key={c.id} className="flex items-center gap-2 p-2 bg-black/40 border border-white/10 rounded" data-testid={`candidate-${c.id}`}>
                  <UserAvatar src={c.profile_photo_url} fallback={(c.name || "?").slice(0, 2)} className="w-8 h-8 rounded-full" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{c.name || "?"}</p>
                    <p className="text-[10px] text-gray-500 uppercase">{c.role}</p>
                  </div>
                  <Button size="sm" onClick={() => grant(c.id)} className="bg-[#CCFF00] text-black hover:bg-[#B3E600] h-8 text-[10px] uppercase" data-testid={`candidate-add-${c.id}`}>
                    <UserPlus size={12} className="mr-1" /> Autorizar
                  </Button>
                </li>
              ))}
            </ul>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="text-xs uppercase">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
